import { ArrowRight, Phone } from "lucide-react";

import { SITE_BRAND_DISPLAY_NAME } from "@/config/siteBrand";

export default function Hero({ t }) {
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative bg-white border-b border-[#e2e6ec] overflow-hidden"
    >
      {/* Background graph paper */}
      <div
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #e2e6ec 1px, transparent 1px), linear-gradient(to bottom, #e2e6ec 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse at top right, black 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at top right, black 30%, transparent 75%)",
        }}
      />
      {/* Red accent corner */}
      <div className="absolute top-0 right-0 w-2 h-32 bg-[#e3191b] hidden lg:block" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-16 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div
            data-testid="hero-eyebrow"
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-6 flex items-center gap-3"
          >
            <span className="w-12 h-px bg-[#e3191b]" />
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] leading-[0.85] tracking-tighter text-6xl sm:text-7xl lg:text-8xl xl:text-9xl"
          >
            {t.hero.title1}{" "}
            <span className="text-[#0b378c]">{t.hero.title2}</span>
            <span className="text-[#e3191b]">.</span>
          </h1>

          <p
            data-testid="hero-lead"
            className="mt-8 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.hero.lead}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => goTo("booking")}
              className="group inline-flex items-center justify-center gap-3 bg-[#e3191b] hover:bg-[#c01416] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => goTo("services")}
              className="inline-flex items-center justify-center gap-3 border-2 border-[#0b378c] hover:bg-[#0b378c] hover:text-white text-[#0b378c] font-bold uppercase tracking-wider text-sm px-7 py-4 transition-colors"
            >
              {t.hero.ctaSecondary}
            </button>
            <a
              data-testid="hero-cta-call"
              href="tel:+40343101131"
              className="inline-flex items-center justify-center gap-3 text-slate-700 hover:text-[#0b378c] font-mono text-sm tracking-widest uppercase px-2 py-4 transition-colors"
            >
              <Phone className="w-4 h-4" />
              +40 343 101 131
            </a>
          </div>
        </div>

        {/* Right: real photo of Selena Service building */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[16/10] lg:aspect-[3/2] overflow-hidden border border-[#e2e6ec] shadow-xl">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: "url('/resources/selena-fatada-front.png')",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b378c]/55 via-[#0b378c]/10 to-transparent" />
            <div className="absolute top-4 left-4 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#0b378c] border-l-2 border-[#e3191b]">
              Slobozia · IL
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-[#0b378c] text-white px-5 py-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 mb-1">
                {t.hero.photoBadge}
              </div>
              <div className="font-[Barlow_Condensed] text-2xl uppercase tracking-tight leading-tight">
                <strong className="font-black">{SITE_BRAND_DISPLAY_NAME}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative bg-[#0b378c] border-t border-[#082a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
          {[
            { v: t.hero.stat1, l: t.hero.stat1Label },
            { v: t.hero.stat2, l: t.hero.stat2Label },
            { v: t.hero.stat3, l: t.hero.stat3Label },
            { v: t.hero.stat4, l: t.hero.stat4Label },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className="py-7 px-4 border-b lg:border-b-0 lg:border-r last:border-r-0 border-white/15"
            >
              <div className="font-[Barlow_Condensed] font-bold text-4xl lg:text-5xl text-white leading-none">
                {s.v}
              </div>
              <div className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-white/60">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
